import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import MaskedInput from 'react-input-mask';
import Drawer from 'Components/Drawer';
import Icon from 'Components/Icon';
import FastDepositConfirmModal from 'Components/FastDeposit/ConfirmModal';
import FastDepositErrorModal from 'Components/FastDeposit/ErrorModal';
import FastDepositActionSheet from 'Components/FastDeposit/ActionSheet';
import HowToDepositSheet from 'Components/FastDeposit/HowToDepositSheet';
import ToastTemplate from 'Components/ToastTemplate';
import useUserTheme from 'Components/Theme/useUserTheme';
import EnvironmentManager from 'Utils/environmentManager';
import { ibanFormatter } from 'Utils/ibanFormatter';
import {
  doubleDigitDecimalFormatter,
  thousandFormatter,
  createAmountChangeHandler,
  createDecimalChangeHandler
} from 'Utils/decimalFormatter';
import { getBankAccounts, createPaymentRequest } from 'Store/actions';
import {
  getHowToDepositPlayClickPoint,
  getLowBalanceCardSubtitle,
  getLowBalanceDescription,
  getLowBalanceFastDepositImage,
  getLowBalanceOnboardingVideoUrl,
  isLowBalanceFastDepositFeatureEnabled,
  trackClickDepositMoneyButton,
  trackClickDepositPageButton,
  trackClickSaveCouponButton,
  trackClickSendToBilyonerFromLowBalance,
  trackViewLowBalanceScreen,
} from 'Utils/lowBalanceFastDeposit';
import { getThemedCmsText } from 'Utils/theme';

const cms = key => EnvironmentManager.getMainMessageText({ key });

const DRAWER_PROPS = {
  position: 'center',
  centered: true,
  modalSize: 'large',
  closeIconVisible: true,
  lockBackgroundScroll: true,
};

const SimpleLayout = ({ onClose, onSaveCoupon, onDepositClick, gameType }) => {
  const isMobileDevice = useSelector(state => state.userAgent?.device?.type === 'mobile');
  const [isHowToSheetOpen, setIsHowToSheetOpen] = useState(false);
  const CMS_TITLE = cms('low_balance_fast_deposit_title') || 'Bakiyen Yetersiz!';
  const CMS_DESCRIPTION = getLowBalanceDescription(cms);
  const CMS_BUTTON_1 = cms('low_balance_fast_deposit_button_1') || 'Kuponu Kaydet';
  const CMS_BUTTON_3 = cms('low_balance_fast_deposit_button_3') || 'Bakiye Yükle';
  const CMS_VIDEO_URL = getLowBalanceOnboardingVideoUrl();
  const CMS_VIDEO_TEXT = cms('low_balance_fast_deposit_onboarding_video_text') || 'Hızlı para yatırma nedir?';
  const CMS_VIDEO_BUTTON = cms('low_balance_fast_deposit_onboarding_video_button') || 'İzle';
  const CMS_IMAGE = getLowBalanceFastDepositImage();
  const drawerProps = isMobileDevice
    ? { position: 'bottom' }
    : { position: 'center', centered: true, modalSize: 'large' };

  useEffect(() => {
    trackViewLowBalanceScreen({ isFullScreen: false });
  }, []);

  return (
    <>
    <Drawer
      closeIconVisible={ DRAWER_PROPS.closeIconVisible }
      lockBackgroundScroll={ DRAWER_PROPS.lockBackgroundScroll }
      { ...drawerProps }
      open={ true }
      onClose={ onClose }
      className="insufficient-funds-drawer insufficient-funds-drawer--simple"
    >
      <div className="insufficient-funds-modal__body">
        { CMS_IMAGE ? (
          <img src={ CMS_IMAGE } alt="" className="insufficient-funds-modal__icon insufficient-funds-modal__icon--image" />
        ) : (
          <Icon iconName="svgInsufficientFunds" className="insufficient-funds-modal__icon" svgViewBox="0 0 56 56" />
        ) }
        <p className="insufficient-funds-modal__title" dangerouslySetInnerHTML={ { __html: CMS_TITLE } } />
        <p className="insufficient-funds-modal__description" dangerouslySetInnerHTML={ { __html: CMS_DESCRIPTION } } />
        <div className="insufficient-funds-modal__actions">
          <button
            className="insufficient-funds-modal__btn insufficient-funds-modal__btn--outline"
            onClick={ () => {
              trackClickSaveCouponButton(gameType);
              onClose();
              onSaveCoupon();
            } }
          >
            { CMS_BUTTON_1 }
          </button>
          <button
            className="insufficient-funds-modal__btn insufficient-funds-modal__btn--solid"
            onClick={ () => {
              trackClickDepositMoneyButton(gameType);
              onClose();
              onDepositClick();
            } }
          >
            { CMS_BUTTON_3 }
          </button>
        </div>
        { CMS_VIDEO_URL && (
          <div className="insufficient-funds-modal__video-banner">
            <div className="insufficient-funds-modal__video-banner__left">
              <Icon iconName="svgAnindaParaYatirVideo" className="insufficient-funds-modal__video-banner__icon" />
              <span className="insufficient-funds-modal__video-banner__text">{ CMS_VIDEO_TEXT }</span>
            </div>
            <button
              type="button"
              className="insufficient-funds-modal__video-banner__btn"
              onClick={ () => setIsHowToSheetOpen(true) }
            >
              { CMS_VIDEO_BUTTON }
            </button>
          </div>
        ) }
      </div>
    </Drawer>
    <HowToDepositSheet
      isOpen={ isHowToSheetOpen }
      onClose={ () => setIsHowToSheetOpen(false) }
      onboardingImage={ cms(isMobileDevice ? 'fast_deposit_page_onboarding_image_app' : 'fast_deposit_page_onboarding_image_web') }
      clickPoint={ getHowToDepositPlayClickPoint('lowBalance', gameType) }
    />
    </>
  );
};

SimpleLayout.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSaveCoupon: PropTypes.func.isRequired,
  onDepositClick: PropTypes.func.isRequired,
  gameType: PropTypes.string.isRequired,
};

const FastDepositLayout = ({ onClose, onSaveCoupon, onDepositClick, gameType }) => {
  const dispatch = useDispatch();
  const [theme] = useUserTheme();
  const bankAccount = useSelector(state => state.bankAccount);
  const paymentRequest = useSelector(state => state.paymentRequest);
  const isMobileDevice = useSelector(state => state.userAgent?.device?.type === 'mobile');

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [amountDecimal, setAmountDecimal] = useState('');
  const [amountFocused, setAmountFocused] = useState(false);
  const [decimalFocused, setDecimalFocused] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualIban, setManualIban] = useState('');
  const [ibanFocused, setIbanFocused] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isHowToSheetOpen, setIsHowToSheetOpen] = useState(false);

  const CMS_TITLE = cms('low_balance_fast_deposit_title') || 'Bakiyen Yetersiz!';
  const CMS_DESCRIPTION = getLowBalanceDescription(cms);
  const CMS_BUTTON_1 = cms('low_balance_fast_deposit_button_1') || 'Kuponu Kaydet';
  const CMS_BUTTON_2 = cms('low_balance_fast_deposit_button_2') || 'Para Yatırma Sayfası';
  const CMS_SUBTITLE = getLowBalanceCardSubtitle(cms);
  const FAST_DEPOSIT_MAIN_ICON = cms('fast_deposit_page_main_icon');
  const FAST_DEPOSIT_BRAND_ICON = getThemedCmsText(theme, 'fast_deposit_brand_icon', 'fast_deposit_brand_icon_dark');
  const showBrandIcons = !!(FAST_DEPOSIT_MAIN_ICON && FAST_DEPOSIT_BRAND_ICON);
  const FAST_DEPOSIT_OTHER_ACCOUNT_ICON = getThemedCmsText(
    theme,
    'fast_deposit_page_payment_request_other_account_icon',
    'fast_deposit_page_payment_request_other_account_icon_dark'
  );
  const CMS_INFO = cms('low_balance_fast_deposit_info');
  const CMS_OTHER_ACCOUNT_BUTTON = cms('low_balance_fast_deposit_other_account_button') || 'Farklı Hesaptan İstek Gönder';
  const CMS_IBAN_LABEL = cms('fast_deposit_page_iban_number_label') || 'IBAN Numarası';
  const CMS_AMOUNT_LABEL = cms('fast_deposit_page_amount_send_label') || 'Gönderilecek Tutar';
  const CMS_SEND_BUTTON = cms('fast_deposit_page_send_Bilyoner_button') || "Bilyoner'e Gönder";
  const CMS_SUCCESS_TOAST = cms('fast_deposit_page_request_received_toast');
  const CMS_VIDEO_URL = getLowBalanceOnboardingVideoUrl();
  const CMS_VIDEO_TEXT = cms('low_balance_fast_deposit_onboarding_video_text') || 'Hızlı para yatırma nedir?';
  const CMS_VIDEO_BUTTON = cms('low_balance_fast_deposit_onboarding_video_button') || 'İzle';
  const CMS_IMAGE = getLowBalanceFastDepositImage();

  useEffect(() => {
    trackViewLowBalanceScreen({ isFullScreen: false });
    dispatch(getBankAccounts(true)).then(accounts => {
      if (accounts?.length) {
        const defaultAccount = accounts.find(a => a?.bankAccount?.default) || accounts[0];
        setSelectedAccount(defaultAccount || null);
      } else {
        setShowManualEntry(true);
      }
    });
  }, []);

  const handleAmountChange = createAmountChangeHandler(setAmount, 10);
  const handleAmountDecimalChange = createDecimalChangeHandler(setAmountDecimal);

  const manualIbanClean = manualIban.replace(/ /g, '');
  const isManualIbanValid = manualIbanClean.length === 26;
  const totalAmount = parseFloat(`${amount || '0'}.${amountDecimal || '00'}`);
  const isIbanReady = showManualEntry ? isManualIbanValid : !!selectedAccount;
  const isSubmitDisabled = !isIbanReady || totalAmount <= 0 || !!paymentRequest?.isSubmitting;

  const handleOtherAccountRequest = e => {
    e?.stopPropagation?.();
    setIsDropdownOpen(false);
    setIsActionSheetOpen(true);
  };

  const handleActionSheetConfirmRequest = data => {
    trackClickSendToBilyonerFromLowBalance(gameType);
    setIsActionSheetOpen(false);
    openConfirmModal({
      iban: data.iban,
      amt: data.amount,
      dec: data.amountDecimal,
      bankName: ''
    });
  };

  const handleManualIbanChange = e => {
    setManualIban(e.target.value);
  };

  const handleSelectAccount = account => {
    setSelectedAccount(account);
    setShowManualEntry(false);
    setIsDropdownOpen(false);
  };

  const openConfirmModal = ({ iban, amt, dec, bankName }) => {
    const formattedAmount = doubleDigitDecimalFormatter(`${amt}.${dec || '00'}`);
    setConfirmData({ iban, amount: amt, amountDecimal: dec || '00', formattedAmount, bankName });
    setIsConfirmOpen(true);
  };

  const handleFooterSubmit = () => {
    trackClickSendToBilyonerFromLowBalance(gameType);
    openConfirmModal({
      iban: showManualEntry ? manualIbanClean : selectedAccount?.bankAccount?.iban,
      amt: amount,
      dec: amountDecimal,
      bankName: showManualEntry ? '' : selectedAccount?.bank?.name
    });
  };

  const handleConfirm = async () => {
    setIsConfirmOpen(false);
    const paymentAmount = parseFloat(`${confirmData?.amount}.${confirmData?.amountDecimal}`);
    const result = await dispatch(createPaymentRequest({ iban: confirmData?.iban, paymentAmount }));
    if (result?.success) {
      setAmount('');
      setAmountDecimal('');
      if (showManualEntry) {
        setManualIban('');
      }
      setConfirmData(null);
      toast(
        <ToastTemplate type="success">{ CMS_SUCCESS_TOAST }</ToastTemplate>,
        { toastId: 'lowBalanceFastDeposit' }
      );
    } else if (result?.error) {
      const { domain, code } = result.error;
      setErrorMessage(cms(`${domain}${code}`) || result?.error?.message);
      setIsErrorOpen(true);
    }
  };

  return (
    <>
      <Drawer
        { ...DRAWER_PROPS }
        open={ true }
        onClose={ onClose }
        className="insufficient-funds-drawer insufficient-funds-drawer--fast-deposit"
      >
        <div className="insufficient-funds-modal__body">
          { CMS_IMAGE ? (
            <img src={ CMS_IMAGE } alt="" className="insufficient-funds-modal__icon insufficient-funds-modal__icon--image" />
          ) : (
            <Icon iconName="svgInsufficientFunds" className="insufficient-funds-modal__icon" svgViewBox="0 0 56 56" />
          ) }
          <p className="insufficient-funds-modal__title" dangerouslySetInnerHTML={ { __html: CMS_TITLE } } />
          <p className="insufficient-funds-modal__description" dangerouslySetInnerHTML={ { __html: CMS_DESCRIPTION } } />

          <div className="insufficient-funds-modal__fast-deposit-card">
            <div className="insufficient-funds-modal__card-header">
              { showBrandIcons ? (
                <>
                  <img src={ FAST_DEPOSIT_MAIN_ICON } alt="" className="insufficient-funds-modal__card-icon" />
                  <img src={ FAST_DEPOSIT_BRAND_ICON } alt="Bilyoner Anında" className="insufficient-funds-modal__card-brand" />
                </>
              ) : (
                <Icon iconName="svgAnindaBilyoner" className="insufficient-funds-modal__card-icon" />
              ) }
              <span className="insufficient-funds-modal__card-subtitle">{ CMS_SUBTITLE }</span>
            </div>
            <div className="insufficient-funds-modal__card-separator" />

            <div className="insufficient-funds-modal__card-body">
              { showManualEntry && (
                <div className="insufficient-funds-modal__iban">
                  <p className="insufficient-funds-modal__amount-label">{ CMS_IBAN_LABEL }</p>
                  <div className={ `insufficient-funds-modal__iban-input-wrapper ${ibanFocused || manualIbanClean.length > 2 ? 'insufficient-funds-modal__iban-input-wrapper--filled' : ''}` }>
                    <MaskedInput
                      type="text"
                      inputMode="numeric"
                      className="insufficient-funds-modal__iban-input"
                      mask="TR99 9999 9999 9999 9999 9999 99"
                      maskChar={ null }
                      value={ manualIban }
                      onChange={ handleManualIbanChange }
                      onFocus={ () => setIbanFocused(true) }
                      onBlur={ () => setIbanFocused(false) }
                      placeholder="TR"
                    />
                    { manualIbanClean.length > 2 && (
                      <button
                        type="button"
                        className="insufficient-funds-modal__iban-clear"
                        onClick={ () => setManualIban('') }
                      >
                        <Icon iconName="svgFastDepositIbanClose" />
                      </button>
                    ) }
                  </div>
                </div>
              ) }

              { !showManualEntry && selectedAccount && (
                <div className="insufficient-funds-modal__account-selector">
                  <div
                    className={ `insufficient-funds-modal__account-row${isDropdownOpen ? ' insufficient-funds-modal__account-row--open' : ''}` }
                    onClick={ () => setIsDropdownOpen(v => !v) }
                  >
                    <div className="insufficient-funds-modal__account-row__left">
                      <img
                        src={ selectedAccount?.bank?.logoUrl }
                        alt={ selectedAccount?.bank?.name }
                        className="fast-deposit__account-card__logo"
                      />
                      <div className="fast-deposit__account-card__info">
                        <span className="fast-deposit__account-card__name">{ selectedAccount?.bank?.name }</span>
                        <span className="fast-deposit__account-card__iban">
                          { ibanFormatter(selectedAccount?.bankAccount?.iban) }
                        </span>
                      </div>
                    </div>
                    <div className="insufficient-funds-modal__account-row__chevron">
                      <Icon
                        iconName={ isDropdownOpen ? 'svgArrowUp' : 'svgDownArrow' }
                        className="insufficient-funds-modal__account-row__arrow"
                      />
                    </div>
                  </div>

                  { isDropdownOpen && (
                    <div className="insufficient-funds-modal__account-dropdown">
                      <div className="withdraw__add-account" onClick={ handleOtherAccountRequest }>
                        <div className="withdraw__add-account__left">
                          <img src={ FAST_DEPOSIT_OTHER_ACCOUNT_ICON } alt="" className="withdraw__add-account__icon" />
                          <span className="withdraw__add-account__text">{ CMS_OTHER_ACCOUNT_BUTTON }</span>
                        </div>
                        <Icon iconName="svgSliderNext" className="withdraw__add-account__arrow" />
                      </div>
                      { (bankAccount?.data || []).map(account => (
                        <div
                          key={ account?.bankAccount?.iban }
                          className={ `fast-deposit__account-card ${
                            selectedAccount?.bankAccount?.iban === account?.bankAccount?.iban
                              ? 'fast-deposit__account-card--selected' : ''
                          }` }
                          onClick={ () => handleSelectAccount(account) }
                        >
                          <div className="fast-deposit__account-card__left">
                            <img
                              src={ account?.bank?.logoUrl }
                              alt={ account?.bank?.name }
                              className="fast-deposit__account-card__logo"
                            />
                            <div className="fast-deposit__account-card__info">
                              <span className="fast-deposit__account-card__name">{ account?.bank?.name }</span>
                              <span className="fast-deposit__account-card__iban">
                                { ibanFormatter(account?.bankAccount?.iban) }
                              </span>
                            </div>
                          </div>
                          <div className={ `fast-deposit__account-card__radio ${
                            selectedAccount?.bankAccount?.iban === account?.bankAccount?.iban
                              ? 'fast-deposit__account-card__radio--selected' : ''
                          }` } />
                        </div>
                      )) }
                    </div>
                  ) }
                </div>
              ) }

              <div className="insufficient-funds-modal__amount">
                <p className="insufficient-funds-modal__amount-label">{ CMS_AMOUNT_LABEL }</p>
                <div className="insufficient-funds-modal__amount-inputs">
                  <div className={ `fast-deposit__footer__input-wrapper ${amountFocused || amount ? 'fast-deposit__footer__input-wrapper--filled' : ''}` }>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="fast-deposit__footer__input"
                      value={ amount ? thousandFormatter(amount) : '' }
                      onChange={ handleAmountChange }
                      onFocus={ () => setAmountFocused(true) }
                      onBlur={ () => setAmountFocused(false) }
                      placeholder="0"
                    />
                  </div>
                  <span className="fast-deposit__footer__separator">,</span>
                  <div className={ `fast-deposit__footer__input-wrapper fast-deposit__footer__input-wrapper--decimal ${decimalFocused || amountDecimal ? 'fast-deposit__footer__input-wrapper--filled' : ''}` }>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="fast-deposit__footer__input fast-deposit__footer__input--decimal"
                      value={ amountDecimal }
                      onChange={ handleAmountDecimalChange }
                      onFocus={ () => setDecimalFocused(true) }
                      onBlur={ () => setDecimalFocused(false) }
                      maxLength={ 2 }
                      placeholder="00"
                    />
                  </div>
                  <span className="fast-deposit__footer__currency">TL</span>
                </div>
              </div>

              <button
                className={ `insufficient-funds-modal__send-btn ${isSubmitDisabled ? 'insufficient-funds-modal__send-btn--disabled' : ''}` }
                onClick={ handleFooterSubmit }
                disabled={ isSubmitDisabled }
              >
                { CMS_SEND_BUTTON }
              </button>

              { CMS_INFO && (
                <div className="insufficient-funds-modal__info-row">
                  <Icon iconName="svgFastDepositInfo" className="insufficient-funds-modal__info-icon" />
                  <p
                    className="insufficient-funds-modal__info-text"
                    dangerouslySetInnerHTML={ { __html: CMS_INFO } }
                  />
                </div>
              ) }
            </div>
          </div>

          <div className="insufficient-funds-modal__actions">
            <button
              className="insufficient-funds-modal__btn insufficient-funds-modal__btn--outline"
              onClick={ () => {
                trackClickSaveCouponButton(gameType);
                onClose();
                onSaveCoupon();
              } }
            >
              { CMS_BUTTON_1 }
            </button>
            <button
              className="insufficient-funds-modal__btn insufficient-funds-modal__btn--outline"
              onClick={ () => {
                trackClickDepositPageButton(gameType);
                onClose();
                onDepositClick();
              } }
            >
              { CMS_BUTTON_2 }
            </button>
          </div>
          { CMS_VIDEO_URL && (
            <div className="insufficient-funds-modal__video-banner">
              <div className="insufficient-funds-modal__video-banner__left">
                <Icon iconName="svgAnindaParaYatirVideo" className="insufficient-funds-modal__video-banner__icon" />
                <span className="insufficient-funds-modal__video-banner__text">{ CMS_VIDEO_TEXT }</span>
              </div>
              <button
                type="button"
                className="insufficient-funds-modal__video-banner__btn"
                onClick={ () => setIsHowToSheetOpen(true) }
              >
                { CMS_VIDEO_BUTTON }
              </button>
            </div>
          ) }
        </div>
      </Drawer>

      <FastDepositActionSheet
        isOpen={ isActionSheetOpen }
        onClose={ () => setIsActionSheetOpen(false) }
        onConfirmRequest={ handleActionSheetConfirmRequest }
        isSubmitting={ !!paymentRequest?.isSubmitting }
      />

      <FastDepositConfirmModal
        isOpen={ isConfirmOpen }
        onClose={ () => setIsConfirmOpen(false) }
        onCancel={ () => setIsConfirmOpen(false) }
        onConfirm={ handleConfirm }
        iban={ confirmData?.iban }
        formattedAmount={ confirmData?.formattedAmount }
        bankName={ confirmData?.bankName }
      />
      <FastDepositErrorModal
        isOpen={ isErrorOpen }
        onClose={ () => setIsErrorOpen(false) }
        message={ errorMessage }
      />

      <HowToDepositSheet
        isOpen={ isHowToSheetOpen }
        onClose={ () => setIsHowToSheetOpen(false) }
        onboardingImage={ cms(isMobileDevice ? 'fast_deposit_page_onboarding_image_app' : 'fast_deposit_page_onboarding_image_web') }
        clickPoint={ getHowToDepositPlayClickPoint('lowBalance', gameType) }
      />
    </>
  );
};

FastDepositLayout.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSaveCoupon: PropTypes.func.isRequired,
  onDepositClick: PropTypes.func.isRequired,
  gameType: PropTypes.string.isRequired,
};

const InsufficientFundsModal = ({ onClose, onSaveCoupon, onDepositClick, gameType = 'iddaa' }) => {
  const showBilyonerAninda = isLowBalanceFastDepositFeatureEnabled();

  if (showBilyonerAninda) {
    return (
      <FastDepositLayout
        onClose={ onClose }
        onSaveCoupon={ onSaveCoupon }
        onDepositClick={ onDepositClick }
        gameType={ gameType }
      />
    );
  }

  return (
    <SimpleLayout
      onClose={ onClose }
      onSaveCoupon={ onSaveCoupon }
      onDepositClick={ onDepositClick }
      gameType={ gameType }
    />
  );
};

InsufficientFundsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSaveCoupon: PropTypes.func.isRequired,
  onDepositClick: PropTypes.func.isRequired,
  gameType: PropTypes.string,
};

export default InsufficientFundsModal;
